// Original, mellow Japanese-inspired synthwave. No recordings or runtime downloads.
// Sparse five-note motifs, warm seventh chords, soft drums and stereo tape-style echoes.
export const TRACKS = [
  { title: 'Kabukicho After Rain', bpm: 82 },
  { title: 'Last Train to Shinjuku', bpm: 88 },
] as const;
const BARS = 64, STEPS = BARS * 16;
const durations = TRACKS.map(t => BARS * 4 * 60 / t.bpm);
const cycleDuration = durations.reduce((a, b) => a + b, 0);
const CHORDS = [
  [57, 60, 64, 71], [53, 57, 60, 64], [55, 60, 64, 71], [55, 59, 62, 69],
  [57, 60, 64, 71], [53, 57, 60, 64], [53, 57, 62, 64], [55, 59, 64, 67],
];
const ROOTS = [45, 41, 48, 43, 45, 41, 38, 40];
// A–B–C–E–F motifs: leave space for the city rather than running a constant lead.
const MOTIFS = [
  [69, 71, 72, 76, 72, 71, 69, 77],
  [76, 72, 71, 69, 71, 72, 77, 76],
];
const freq = (note: number) => 440 * 2 ** ((note - 69) / 12);

/** Shared wall-clock position. Joining late and changing tempo never restart the playlist. */
export function musicPosition(seconds: number) {
  const time = Math.max(0, seconds);
  const local = time % cycleDuration;
  const track = local < durations[0] ? 0 : 1;
  const start = time - local + (track ? durations[0] : 0);
  const stepSeconds = 60 / TRACKS[track].bpm / 4;
  const step = Math.min(STEPS - 1, Math.floor((time - start) / stepSeconds + 1e-6));
  return { track, step, stepSeconds, next: start + (step + 1) * stepSeconds };
}

type Part = 'pad' | 'bass' | 'pluck' | 'kick' | 'rim' | 'hat';
export interface MusicNote { part: Part; note: number; duration: number; gain: number; pan: number }

/** A 64-bar arrangement: introduction, two evolving verses, a quiet break and a return. */
export function musicNotes(track: number, step: number): MusicNote[] {
  const i = ((step % STEPS) + STEPS) % STEPS;
  const bar = Math.floor(i / 16), beat = i % 16;
  const sd = 60 / TRACKS[track].bpm / 4;
  const chord = Math.floor(bar / 2) % CHORDS.length;
  const intro = bar < 8, breakDown = bar >= 40 && bar < 48;
  const groove = !intro && !breakDown;
  const notes: MusicNote[] = [];
  const add = (part: Part, note: number, duration: number, gain: number, pan = 0) => notes.push({ part, note, duration, gain, pan });
  if (i % 32 === 0) CHORDS[chord].forEach((n, k) => add('pad', n, sd * 31, 0.035, (k % 2 ? 1 : -1) * 0.38));
  if (beat === 0 || (groove && beat === 10)) add('bass', ROOTS[chord] + (beat ? 7 : 0), sd * (beat ? 4 : 7), 0.14);
  if (groove && (beat === 0 || beat === 8 || (track === 1 && beat === 14 && bar % 4 === 3))) add('kick', 0, 0.22, 0.24);
  if (groove && (beat === 4 || beat === 12)) add('rim', 0, 0.09, 0.052, 0.13);
  if (groove && beat % 4 === 2) add('hat', 0, beat === 14 ? 0.10 : 0.035, 0.025, beat % 8 ? -0.3 : 0.3);
  // A quiet pulse underneath the plucked motif, with rests and no piercing square-wave lead.
  if (bar >= 8 && !breakDown && beat === 6 && bar % 2 === 1) add('pluck', CHORDS[chord][2] + 12, sd * 2, 0.038, -0.42);
  if (beat === 2 || (beat === 11 && bar % 2 === 0)) {
    const phrase = Math.floor(bar / 8);
    const n = MOTIFS[track][(bar + (beat === 11 ? 3 : 0) + phrase * 2) % 8];
    add('pluck', n, sd * (beat === 2 ? 4.5 : 2.5), intro || breakDown ? 0.075 : 0.09, beat === 2 ? 0.22 : -0.22);
  }
  return notes;
}

interface Voice { sources: AudioScheduledSourceNode[]; nodes: AudioNode[]; gain: GainNode }

export class Synthwave {
  private input: GainNode;
  private output: GainNode;
  private echo: GainNode;
  private nodes: AudioNode[] = [];
  private voices = new Set<Voice>();
  private noise: AudioBuffer;
  private next = -1;
  private disposed = false;

  constructor(private ctx: BaseAudioContext, destination: AudioNode) {
    this.input = ctx.createGain(); this.output = ctx.createGain(); this.echo = ctx.createGain();
    this.output.gain.value = 0.85; this.echo.gain.value = 0.22;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 28;
    const soft = ctx.createBiquadFilter(); soft.type = 'highshelf'; soft.frequency.value = 2600; soft.gain.value = -4;
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -16; comp.knee.value = 18;
    comp.ratio.value = 2.5; comp.attack.value = 0.008; comp.release.value = 0.22;
    this.input.connect(hp).connect(soft).connect(comp).connect(this.output).connect(destination);
    this.nodes.push(this.input, hp, soft, comp, this.output, this.echo);
    // Dark, quiet stereo echoes. Feedback stays well below unity and bass/drums stay dry.
    for (const [delaySeconds, pan] of [[0.31, -0.6], [0.47, 0.6]]) {
      const delay = ctx.createDelay(1); delay.delayTime.value = delaySeconds;
      const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 2300;
      const feedback = ctx.createGain(); feedback.gain.value = 0.18;
      const panner = ctx.createStereoPanner(); panner.pan.value = pan;
      this.echo.connect(delay).connect(filter).connect(panner).connect(this.input);
      filter.connect(feedback).connect(delay);
      this.nodes.push(delay, filter, feedback, panner);
    }
    this.noise = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.3), ctx.sampleRate);
    const data = this.noise.getChannelData(0); let seed = 2089;
    for (let i = 0; i < data.length; i++) { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; data[i] = seed / 2147483648 - 1; }
  }

  /** Schedule only a short lookahead; background-tab stalls skip ahead rather than catching up. */
  tick(seconds: number) {
    if (this.disposed || this.ctx.state !== 'running' || !Number.isFinite(seconds)) return;
    const pos = Math.max(0, seconds);
    if (this.next < pos || this.next > pos + 0.4) this.next = musicPosition(pos).next;
    while (this.next < pos + 0.2) {
      const p = musicPosition(this.next + 1e-5);
      this.scheduleStep(p.track, p.step, this.ctx.currentTime + this.next - pos);
      this.next = p.next;
    }
  }

  /** Also used by the offline listening-preview renderer; exactly the same instruments as the game. */
  scheduleStep(track: number, step: number, at: number) {
    if (this.disposed) return;
    for (const n of musicNotes(track, step)) this.voice(n, at);
  }

  private voice(n: MusicNote, at: number) {
    const ctx = this.ctx, t = Math.max(ctx.currentTime, at);
    const g = ctx.createGain(), p = ctx.createStereoPanner(), filter = ctx.createBiquadFilter();
    p.pan.value = n.pan; filter.type = 'lowpass'; filter.Q.value = 0.45;
    filter.frequency.value = n.part === 'pad' ? 1000 : n.part === 'bass' ? 420 : 2600;
    const release = n.part === 'pad' ? 0.6 : n.part === 'pluck' ? 0.38 : 0.07;
    const attack = n.part === 'pad' ? 0.45 : 0.009;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(n.gain, t + Math.min(attack, n.duration / 3));
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, n.gain * (n.part === 'pad' ? 0.72 : 0.14)), t + n.duration);
    g.gain.exponentialRampToValueAtTime(0.0001, t + n.duration + release);
    g.gain.linearRampToValueAtTime(0, t + n.duration + release + 0.02);
    filter.connect(g).connect(p).connect(this.input);
    if (n.part === 'pad' || n.part === 'pluck') p.connect(this.echo);
    const sources: AudioScheduledSourceNode[] = [], nodes: AudioNode[] = [filter, g, p];
    const oscillator = (type: OscillatorType, hz: number, detune = 0) => {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz; o.detune.value = detune;
      sources.push(o); nodes.push(o); return o;
    };
    if (n.part === 'pad') {
      oscillator('triangle', freq(n.note), -7).connect(filter);
      oscillator('sawtooth', freq(n.note), 7).connect(filter);
    } else if (n.part === 'bass') {
      oscillator('triangle', freq(n.note)).connect(filter);
      const sub = ctx.createGain(); sub.gain.value = 0.45; nodes.push(sub);
      oscillator('sine', freq(n.note - 12)).connect(sub).connect(filter);
    } else if (n.part === 'pluck') {
      const carrier = oscillator('sine', freq(n.note));
      const mod = oscillator('sine', freq(n.note) * 2);
      const depth = ctx.createGain(); depth.gain.setValueAtTime(freq(n.note) * 0.45, t);
      depth.gain.exponentialRampToValueAtTime(0.5, t + 0.23); nodes.push(depth);
      mod.connect(depth).connect(carrier.frequency); carrier.connect(filter);
    } else if (n.part === 'kick') {
      filter.frequency.value = 700;
      const o = oscillator('sine', 125); o.frequency.setValueAtTime(125, t);
      o.frequency.exponentialRampToValueAtTime(44, t + 0.11); o.connect(filter);
    } else {
      const s = ctx.createBufferSource(); s.buffer = this.noise; sources.push(s); nodes.push(s);
      filter.type = 'bandpass'; filter.frequency.value = n.part === 'rim' ? 1350 : 6300;
      filter.Q.value = n.part === 'rim' ? 1.8 : 0.55; s.connect(filter);
      if (n.part === 'rim') oscillator('triangle', 180).connect(filter);
    }
    const voice = { sources, nodes, gain: g }; this.voices.add(voice);
    let ended = 0;
    for (const s of sources) {
      s.onended = () => {
        if (++ended !== sources.length) return;
        for (const node of nodes) node.disconnect();
        this.voices.delete(voice);
        if (this.disposed && !this.voices.size) this.disconnect();
      };
      s.start(t); s.stop(t + n.duration + release + 0.03);
    }
  }

  /** Cancel queued voices and fade the whole bus, including echoes, when changing stations. */
  dispose() {
    if (this.disposed) return; this.disposed = true;
    const t = this.ctx.currentTime;
    this.output.gain.cancelScheduledValues(t); this.output.gain.setValueAtTime(this.output.gain.value, t);
    this.output.gain.linearRampToValueAtTime(0, t + 0.06);
    for (const v of this.voices) for (const s of v.sources) s.stop(t + 0.065);
    if (!this.voices.size) this.disconnect();
  }
  private disconnect() { for (const n of this.nodes) n.disconnect(); this.nodes = []; }
}
